<template>
    <div>
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >

            <template slot="trailing">
                <rkm-button
                    :title="filtroVisible ? 'Ocultar' : 'Exibir'"
                    @click="filtroVisible = !filtroVisible"
                    color="primary"
                    small
                >
                    <rkm-icon :icon="`angle-double-${filtroVisible ? 'up' : 'down'}`" />
                </rkm-button>
            </template>

            <component
                :is="'rkm-collapse'"
                :visible="filtroVisible"
            >
                <rkm-form
                    ref="Form"
                    show-legend
                >
                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-date-picker
                                range
                                :range-inline="false"
                                start-label="Período de"
                                end-label="Até"
                                v-model="model.periodo"
                                :start-validations="validations.periodo.start"
                                :end-validations="validations.periodo.end"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                defaultOptionText="Todas"
                                :options="unidadesSolicitante"
                                label="Unidade solicitante"
                                show-default-option
                                text-field="unidadeSolicitanteNome"
                                v-model="model.unidadeSolicitanteId"
                                value-field="unidadeSolicitanteId"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                defaultOptionText="Todas"
                                :options="unidades"
                                label="Unidade executante"
                                show-default-option
                                text-field="unidadeNome"
                                v-model="model.unidadeId"
                                value-field="unidadeId"
                                :validations="validations.unidadeId"
                            />
                        </rkm-col>

                        <rkm-col cols="8">
                            <rkm-form-select
                                defaultOptionText="Todos"
                                :options="profissionais"
                                label="Profissional executante"
                                show-default-option
                                text-field="nome"
                                v-model="model.profissionalId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                label="Grupo de exames"
                                :options="grupos"
                                v-model="model.grupoId"
                                show-default-option
                                default-option-text="Todos"
                                text-field="nome"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="exames"
                                defaultOptionText="Todos"
                                label="Exames"
                                show-default-option
                                text-field="exameNome"
                                v-model="model.exameId"
                                value-field="exameId"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="status"
                                defaultOptionText="Todos"
                                label="Status"
                                show-default-option
                                text-field="statusNome"
                                v-model="model.statusId"
                                value-field="statusId"
                            />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-input
                                label="Prontuário"
                                v-model="model.prontuario"
                                number
                            />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-input
                                label="Código do exame"
                                v-model="model.procedimento"
                                number
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>

                        <rkm-col class="flex-grow-1">
                            <rkm-form-input-autocomplete
                                :repository="repositories.paciente"
                                :search-fields="fieldsPaciente.paciente"
                                :text-field="(item) => item.cidadao.nome"
                                label="Paciente"
                                v-model="paciente"
                                uppercase
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-button
                                :label="false"
                                color="primary"
                                class="float-left"
                                @click="$refs.Form.validate(search)"
                            >
                                Buscar
                            </rkm-button>

                            <rkm-button
                                :label="false"
                                @click="resetForm()"
                                class="mr-2 float-left"
                            >
                                Limpar
                            </rkm-button>
                        </rkm-col>
                    </rkm-row>
                </rkm-form>
            </component>
        </rkm-area>

        <rkm-area
            title="Totais"
            type="fieldset"
            no-margin-top
            v-if="notSaveLancamentos"
        >
            <rkm-row>
                <rkm-col cols="2">
                    Paciente
                    <rkm-alert color="secondary">
                        <strong>
                            {{ totalPaciente() }}
                        </strong>
                    </rkm-alert>
                </rkm-col>

                <rkm-col cols="2">
                    Exame
                    <rkm-alert color="secondary">
                        <strong>
                            {{ totalExames() }}
                        </strong>
                    </rkm-alert>
                </rkm-col>

                <rkm-col cols="2">
                    Valor
                    <rkm-alert color="secondary">
                        <strong>
                            {{ formatarMoeda(totalValorExames()) }}
                        </strong>
                    </rkm-alert>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            v-if="notSaveLancamentos"
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    no-caret
                    small
                    title="Colunas"
                    cols="1"
                    v-rkm-tooltip.hover
                    menu-class="auto-scroll"
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="notSaveLancamentos.length === 0"
                    @click="impressao()"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="notSaveLancamentos.length === 0"
                    @click="exportar()"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :items="notSaveLancamentos"
                :fields="fields"
                ref="Table"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
                hide-filters
            >

                <template v-slot:cell(pacienteNome)="row">
                    <rkm-button
                        class="mr-2"
                        color="secondary"
                        small
                        v-rkm-popover.hover.left="
                            {
                                title: 'Dados cadastrais',
                                html: true,
                                content:
                                    '<b>Prontuário:</b> ' + (row.item.prontuario || '') + '<br/>' +
                                    '<b>Data de Nascimento:</b> ' + row.item.pacienteDataNascimento + '<br/>' +
                                    '<b>Nome da Mãe:</b> ' + row.item.nomeMae + '<br/>'
                            }
                        "
                    >
                        <rkm-icon icon="user" />
                    </rkm-button>

                    <span v-if="!row.item.sequencialDesconhecido">
                        {{row.item.pacienteNome}}
                    </span>

                    <span
                        v-else
                        class="text-primary"
                        v-rkm-popover.hover.top="
                            {
                                title: 'Características físicas',
                                content: row.item.caracteristicasFisicas
                            }
                        "
                    >
                        {{row.item.pacienteNome}}
                    </span>
                </template>

                <template v-slot:cell(pacienteCpf)="row">
                        {{ row.value | formatCpf }}
                    </template>
                    <template v-slot:cell(pacienteCns)="row">
                        {{ row.value | formatCns }}
                    </template>

                <template v-slot:cell(nomeProcedimento)="row">
                    <template v-if="row.item.nomeProcedimento === null || row.item.nomeProcedimento === ''|| row.item.nomeProcedimento === 0">
                       SEM PROCEDIMENTO
                    </template>
                    <template v-else>
                        {{ row.item.nomeProcedimento }}
                    </template>
                </template>

                <template v-slot:cell(exameCodigo)="row">
                    <template v-if="row.item.exameCodigo === null || row.item.exameCodigo === ''|| row.item.exameCodigo === 0">
                        -
                    </template>
                    <template v-else>
                        {{ row.item.exameCodigo }}
                    </template>
                </template>
            </rkm-table>
        </rkm-area>

        <ModalPreferenciaImpressao ref="ModalPreferenciaImpressao" @orientation="updateOrientation"/>
    </div>
</template>

<script>

// Components.
import ModalPreferenciaImpressao from '@/components/geral/impressao/Modal'

// Mixins.
import { repository, error, object, data, stringCase, file} from '@/mixins'

// Enuns.
import statusFechamento from '@/enums/exame-imagem/fechamento/status'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

// Filters.
import formats from '@/filters/formats'

export default {
    components:{ ModalPreferenciaImpressao },
    created() {

        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.exameImagem'),
            exportacao: this.getConectaSusRepository('exportacao.exameImagem'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            relatorio: this.getConectaSusRepository('exameImagem.relatorioExameStatus'),
        }
        this.load()
    },
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        fieldsPaciente(){

            return {
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        validations() {
            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        },
        unidadesSolicitante(){

            let unidadeSolicitanteOrigem = []

            if(this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.relatorioExameStatus
                    .filter(agendamento => moment(agendamento.data, 'DD/MM/YYYY').isBetween(start, end))
                    .map(unidade => {

                        let verifyUnidade = unidadeSolicitanteOrigem.find(u => u.unidadeSolicitanteId === unidade.unidadeSolicitanteId)

                        if(!verifyUnidade){

                            unidadeSolicitanteOrigem.push({

                                unidadeSolicitanteId: unidade.unidadeSolicitanteId,
                                unidadeSolicitanteNome: unidade.unidadeSolicitanteNome
                            })
                        } else {

                            this.resetUnidadesSolicitante()
                            this.resetDatatable()
                        }
                    })
            }

            return unidadeSolicitanteOrigem
        },
        unidades(){

            let unidadeOrigem = []

            if(this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.relatorioExameStatus
                    .filter(agendamento =>
                        moment(agendamento.data, 'DD/MM/YYYY').isBetween(start, end)
                        && (this.model.unidadeSolicitanteId === null || agendamento.unidadeSolicitanteId === this.model.unidadeSolicitanteId)
                    )
                    .map(unidade => {

                        let verifyUnidade = unidadeOrigem.find(u => u.unidadeId === unidade.unidadeId)

                        if(!verifyUnidade){

                            unidadeOrigem.push({

                                unidadeId: unidade.unidadeId,
                                unidadeNome: unidade.nomeFantasia
                            })
                        }
                    })
            }

            return unidadeOrigem
        },
        profissionais() {
            const profissionaisOrigem = [];

            if (this.model.periodo.start && this.model.periodo.end) {
                const start = moment(this.model.periodo.start).subtract(1, 'day');
                const end = moment(this.model.periodo.end).add(1, 'day');

                const filters = [
                    e => moment(e.data, 'DD/MM/YYYY').isBetween(start, end),
                    e => this.model.unidadeSolicitanteId === null || e.unidadeSolicitanteId === this.model.unidadeSolicitanteId,
                    e => this.model.unidadeId === null || e.unidadeId === this.model.unidadeId
                ];

                this.data.model.relatorioExameStatus
                    .filter(grupo => filters.every(filter => filter(grupo)))
                    .forEach(grupo => {
                        if (!profissionaisOrigem.some(g => g.id === grupo.profissionalId)) {
                            profissionaisOrigem.push({
                                id: grupo.profissionalId,
                                nome: grupo.profissionalNome
                            });
                        }
                    });

                profissionaisOrigem.sort((a, b) => a.nome.localeCompare(b.nome));
            }

            return profissionaisOrigem;
        },
        grupos() {
            const gruposOrigem = [];

            if (this.model.periodo.start && this.model.periodo.end) {
                const start = moment(this.model.periodo.start).subtract(1, 'day');
                const end = moment(this.model.periodo.end).add(1, 'day');

                const filters = [
                    e => moment(e.data, 'DD/MM/YYYY').isBetween(start, end),
                    e => this.model.unidadeSolicitanteId === null || e.unidadeSolicitanteId === this.model.unidadeSolicitanteId,
                    e => this.model.unidadeId === null || e.unidadeId === this.model.unidadeId
                ];

                this.data.model.relatorioExameStatus
                    .filter(grupo => filters.every(filter => filter(grupo)))
                    .forEach(grupo => {
                        if (!gruposOrigem.some(g => g.id === grupo.grupoExameId)) {
                            gruposOrigem.push({
                                id: grupo.grupoExameId,
                                nome: grupo.grupo
                            });
                        }
                    });

                gruposOrigem.sort((a, b) => a.nome.localeCompare(b.nome));
            }

            return gruposOrigem;
        },
        exames() {
            const exameOrigem = [];

            if (this.model.periodo.start && this.model.periodo.end) {
                const start = moment(this.model.periodo.start).subtract(1, 'day');
                const end = moment(this.model.periodo.end).add(1, 'day');

                const filters = [
                    e => moment(e.data, 'DD/MM/YYYY').isBetween(start, end),
                    e => this.model.unidadeSolicitanteId === null || e.unidadeSolicitanteId === this.model.unidadeSolicitanteId,
                    e => this.model.unidadeId === null || e.unidadeId === this.model.unidadeId,
                    e => this.model.grupoId === null || e.grupoExameId === this.model.grupoId
                ];

                if (this.verificaUnidade) {
                    filters.push(e => this.model.unidadeId === null || e.unidadeId === this.model.unidadeId);
                }

                this.data.model.relatorioExameStatus
                    .filter(exame => filters.every(filter => filter(exame)))
                    .forEach(exame => {
                        if (!exameOrigem.some(e => e.exameId === exame.exameId)) {
                            exameOrigem.push({
                                exameId: exame.exameId,
                                exameNome: exame.exameNome
                            });
                        }
                    });

                exameOrigem.sort((a, b) => a.exameNome.localeCompare(b.exameNome));
            }

            return exameOrigem;
        },
        status() {
            const statusOrigem = [];

            if (this.model.periodo.start && this.model.periodo.end) {

                const start = moment(this.model.periodo.start).subtract(1, 'day');
                const end = moment(this.model.periodo.end).add(1, 'day');

                const filters = [
                    e => moment(e.data, 'DD/MM/YYYY').isBetween(start, end),
                    e => this.model.unidadeSolicitanteId === null || e.unidadeSolicitanteId === this.model.unidadeSolicitanteId,
                    e => this.model.exameId === null || e.exameId === this.model.exameId,
                    e => this.model.unidadeId === null || e.unidadeId === this.model.unidadeId,
                    e => this.model.grupoId === null || e.grupoExameId === this.model.grupoId
                ];

                this.data.model.relatorioExameStatus
                    .filter(status => filters.every(filter => filter(status)))
                    .forEach(status => {
                        status.statusId = status.statusId ?? this.enums.statusFechamento.AGENDADO;

                        if (!statusOrigem.some(s => s.statusId === status.statusId)) {
                            statusOrigem.push({
                                statusId: status.statusId,
                                statusNome: status.statusNome
                            });
                        }
                    });

                statusOrigem.sort((a, b) => a.statusNome.localeCompare(b.statusNome));
            }

            return statusOrigem;
        }
    },
    data: () => ({
        orientation: '',
        notSaveLancamentos: null,
        paciente: null,
        model: {
            prontuario: '',
            periodo: {
                start: null,
                end: null,
            },            
            exameId: null,
            unidadeId: null,
            unidadeSolicitanteId: null,
            statusId: null,
            procedimento: '',
            grupoId: null,
            profissionalId: null,
        },
        sortBy: 'data',
        sortDesc: false,
        indeterminate: false,
        additionals: {},
        enums:{
            statusFechamento,
        },
        selectedCols: [],
        selectedAll: false,
        filtroVisible: true,
        columns: [
            { key: 'data', label: 'Data do exame', sortable: true, class: 'col-min text-center', fixed: true },
            { key: 'pacienteNome', label: 'Paciente', sortable: true, fixed: true },
            { key: 'prontuario', label: 'Prontuário', sortable: true, class: 'col-min text-center', fixed: true },
            { key: 'pacienteDataNascimento', label: 'Data de nascimento', sortable: true, class: 'col-min text-center', fixed: false},
            { key: 'pacienteCns', label: 'CNS', sortable: true, class: 'col-min text-center', fixed: false},
            { key: 'pacienteCpf', label: 'CPF', sortable: true, class: 'col-min text-center', fixed: false},
            { key: 'pacienteRacaCor', label: 'Raça/Cor', sortable: true, fixed: false},
            { key: 'pacienteSexo', label: 'Sexo', sortable: true, fixed: false},
            { key: 'grupo', label: 'Grupo', sortable: true, fixed: false},
            { key: 'exameNome', label: 'Exame', sortable: true, fixed: true },
            { key: 'codigoProcedimento', label: 'Código do exame', sortable: true, class: 'col-min text-center', fixed: false},
            { key: 'nomeProcedimento', label: 'Procedimento', sortable: true, fixed: false},
            { key: 'exameValor', label: 'Valor do exame', sortable: true, class: 'col-min text-center', fixed: false},
            { key: 'nomeFantasia', label: 'Unidade executante', sortable: true, fixed: true},
            { key: 'statusNome', label: 'Status', sortable: true, fixed: true},
            { key: 'profissionalExecutante', label: 'Profissional executante', sortable: true, fixed: true},
            { key: 'unidadeSolicitanteNome', label: 'Unidade solicitante', sortable: true, fixed: false},
            { key: 'nomeAgendador', label: 'Usuário da solicitação', sortable: true, fixed: false},
            { key: 'dataHoraSolicitacao', label: 'Data/hora solicitação', sortable: true, class: 'col-min text-center', fixed: false},
        ],
        verificaUnidade: false,
        verificaExame: false,
    }),
    filters: {
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.model.periodo.start = this.data.model.periodo.start
            this.model.periodo.end = this.data.model.periodo.end
        },
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        search() {

            this.setLoading()

            this.repositories.relatorio.datatable(this.getSearchParams())
                .then(({ data }) => {

                    this.notSaveLancamentos = data
                    this.handleSelectedColumns()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        setDefaultValues() {
            this.model.periodo.start = this.data.model.periodo.start
            this.model.periodo.end = this.data.model.periodo.end
            this.resetDatatable()
        },
        getSearchParams() {
            const params = this.clone(this.model)

            if (this.paciente) {

                params.pacienteId = this.paciente.id
                params.pacienteNome = this.paciente.cidadao.nome
            }

            this.formateDates(params.periodo, ['start', 'end'], 'DD/MM/YYYY')
            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetDatatable(){
            this.notSaveLancamentos = null
        },
        resetUnidade(){
            this.model.unidadeId = null
        },
        resetUnidadesSolicitante(){
            this.model.unidadeSolicitanteId = null
        },
        resetExames(){
            this.model.exameId = null
        },
        resetStatus(){
            this.model.statusId = null
        },
        resetGrupo(){
            this.model.grupoId = null
        },
        resetProfissional(){
            this.model.profissionalId = null
        },
        resetForm() {

            this.model.prontuario = null
            this.paciente = null
            this.model.exameId = null
            this.model.grupoId = null
            this.model.profissionalId = null
            this.model.dataNascimento = null
            this.model.unidadeId = null
            this.model.unidadeSolicitanteId = null
            this.model.statusId = null
            this.model.procedimento = null

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()
                    this.$refs.Form.resetValidator()
                })
        },
        handleSelectedColumns(selected) {
            if (selected) {
                this.cols.map(col => {

                    this.selectedCols.push(col.value)
                })
            } else {
                this.selectedCols = []
            }
        },
        impressao() {

            this.$refs.ModalPreferenciaImpressao.open();
            this.$refs.ModalPreferenciaImpressao.$once('completed', () => {

                this.setLoading()

                const filter = this.getFilterExportPrint()

                this.repositories.impressao.relatorioExameStatus(filter)
                    .then(response => {

                        const content = new Blob([response.data], {type: response.headers['content-type']})
                        const url = URL.createObjectURL(content)
                        const link = document.createElement('a')
                        link.href = url
                        link.target = '_blank'
                        link.click()
                        link.remove()
                    })
                    .finally(() => this.unsetLoading())
            })
        },
        exportar() {

            this.setLoading()

            const filter = this.getFilterExportPrint()

            this.repositories.exportacao.exportExameStatus(filter)
                .then(response => {

                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .finally(() => this.unsetLoading())
        },
        getFilterExportPrint() {

            const filter = this.getSearchParams()

            const $this = this

            let cols = this.clone(this.fields)
            cols = cols.map(function (field) {

                field.key = $this.camelToSnake(field.key)

                return field
            })

            filter.columns = cols
            filter.orientation = this.orientation

            filter.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            filter.sortDesc = this.sortDesc

            const somaPaciente = [...new Set(this.notSaveLancamentos.map(item => item.pacienteId))].length
            filter.totalizandoPaciente = somaPaciente

            const somaExames = this.notSaveLancamentos.filter(item => item.exameId).length
            filter.totalizandoExames = somaExames

            const valorExames = this.formatarMoeda(this.totalValorExames())
            filter.valorExames = valorExames

            const solicitantes = this.unidadesSolicitante.find(u => u.unidadeSolicitanteId === this.model.unidadeSolicitanteId)
            filter.solicitantes = solicitantes ? solicitantes.unidadeSolicitanteNome : 'Todas'

            const executantes = this.unidades.find(u => u.unidadeId === this.model.unidadeId)
            filter.executantes = executantes ? executantes.unidadeNome : 'Todas'

            const exame = this.exames.find(e => e.exameId === this.model.exameId)
            filter.exame = exame ? exame.exameNome : 'Todos'

            const status = this.status.find(e => e.statusId === this.model.statusId)
            filter.status = status ? status.statusNome : 'Todos'

            const grupo = this.grupos.find(e => e.id === this.model.grupoId)
            filter.grupo = grupo ? grupo.nome : 'Todos'

            const profissional = this.profissionais.find(e => e.id === this.model.profissionalId)
            filter.profissional = profissional ? profissional.nome : 'Todos'

            return filter
        },
        totalPaciente(){
            return  [...new Set(this.notSaveLancamentos.map(item => item.pacienteId))].length
        },
        totalExames(){
            return this.notSaveLancamentos.filter(item => item.exameId).length
        },
        totalValorExames() {
            return this.notSaveLancamentos.reduce((total, lancamento) => {
                let valorStr = (lancamento.exameValor || "0").toString().replace(',', '.');
                let valor = parseFloat(valorStr) || 0;
                return total + valor;
            }, 0);
        },
        formatarMoeda(valor) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(valor);
        },
        updateOrientation(value) {
            this.orientation = value;
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, object, data, stringCase, file],
    watch: {
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
        'model.unidadeSolicitanteId': function () {

            this.resetUnidade();
            this.resetProfissional();
            this.resetGrupo();
            this.resetExames();
            this.resetStatus();
            this.resetDatatable();
        },
        'model.unidadeId': function () {

            this.resetProfissional();
            this.resetGrupo();
            this.resetExames();
            this.resetStatus();
            this.resetDatatable();
        },
        'model.exameId': function () {

            this.resetStatus();
            this.resetDatatable();
        },
        'model.statusId': function () {

            this.resetDatatable();
        },
        'model.profissionalId': function() {

            this.resetGrupo();
            this.resetExames();
            this.resetStatus();
            this.resetDatatable();
        },
        'model.grupoId': function() {

            this.resetExames();
            this.resetStatus();
            this.resetDatatable();
        }
    },
    props: {
        data: { type: Object, required: true }
    }
}
</script>

<style scoped>
    .warning {
        color: var(--warning);
    }
</style>